import { useState } from 'react';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OrganizationRes, BasicUserWithPermissionRolesRes } from '@dealstack/shared/lib/generated';
import { Button, Flex, Badge } from '@mantine/core';
import { PermissionsCell } from '@dealstack/frontend/shared/components/PermissionCell/PermissionsCell';
import { ORGANIZATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { useOrganizationUsers } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationUsers';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	ResourceType,
	getResourceTypePermissions,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { OrganizationSettingsView } from '@dealstack/frontend/app/pages/Organization/Organization.types';
import { GenericError } from '@dealstack/frontend/shared/pages/GenericError/GenericError';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { useInviteOrganizationUser } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationInviteUser';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	createUserNameColumn,
	createUserPermissionsColumn,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/UserColumn';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

interface Props {
	organization: OrganizationRes;
	onViewChange: (view: OrganizationSettingsView, userId: string) => void;
}

export const OrganizationUsers = ({ organization, onViewChange }: Props) => {
	const tOrganization = useTranslationOrganization();
	const [showInviteDrawer, setShowInviteDrawer] = useState(false);

	const {
		data,
		isFetching,
		refetch: refetchOrganizationUsers,
	} = useOrganizationUsers({
		organizationId: organization.id ?? '',
	});

	const { mutate: inviteOrganizationUser, isPending } = useInviteOrganizationUser();

	const handleOrganizationInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!organization.id) {
			showToastV2({
				state: 'error',
				message: 'Missing organisation id',
			});
			return;
		}

		inviteOrganizationUser(
			{
				organizationId: organization.id,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
			},
			{
				onSuccess: async () => {
					await refetchOrganizationUsers();
					onSuccess?.();
				},
			}
		);
	};

	const { mutate: resendInvite } = useInviteOrganizationUser();

	const handleResendInvitation = (member: BasicUserWithPermissionRolesRes) => {
		if (!organization.id) {
			showToastV2({
				state: 'error',
				message: 'Missing organisation id',
			});
			return;
		}

		resendInvite(
			{
				organizationId: organization.id,
				firstName: member.firstName,
				lastName: member.lastName,
				email: member.email,
			},
			{
				onSuccess: () => {
					showToastV2({
						message: tOrganization('members.toast.resend_invitation.success'),
					});
				},
				onError: () => {
					showToastV2({
						state: 'error',
						message: tOrganization('members.toast.resend_invitation.error'),
					});
				},
			}
		);
	};

	const ResendInvitationButton = ({
		user,
		onResendInvitation,
	}: {
		user: BasicUserWithPermissionRolesRes;
		onResendInvitation: (user: BasicUserWithPermissionRolesRes) => void;
	}) => {
		if (user.active) {
			return null;
		}

		return (
			<Flex w="100%" align="center" justify="flex-end">
				<Button
					data-testid={`${ORGANIZATION_ELEMENT_ID.USER_ROW_RESEND_INVITATION}-${user.email}`}
					variant="subtle"
					onClick={(e) => {
						e.stopPropagation();
						onResendInvitation(user);
					}}
				>
					{tOrganization('members.buttons.resend_invitation')}
				</Button>
			</Flex>
		);
	};

	const organizationPermissions = getResourceTypePermissions(ResourceType.ORGANIZATION);

	const permissions = organizationPermissions
		.map((relation) => ({
			label: tOrganization(`invite_team_member.relation.${relation}`),
			value: relation,
		})) // sort by alphabetical order
		.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

	const columns: ExtendedColumnDef<BasicUserWithPermissionRolesRes>[] = [
		createUserNameColumn<BasicUserWithPermissionRolesRes>({
			header: tOrganization('members.table.headers.name'),
			filterPlaceholder: tOrganization('members.table.filter.name'),
			filterText: tOrganization('members.table.filter.name'),
		}),
		{
			...createUserPermissionsColumn<BasicUserWithPermissionRolesRes>({
				id: 'permissions',
				t: tOrganization,
				header: tOrganization('members.table.headers.permissions'),
				filterPlaceholder: tOrganization('members.table.filter.permissions'),
				filterText: tOrganization('members.table.filter.permissions'),
				permissions,
			}),
			cell: ({ row }) => {
				const roles = row.original.permissionRoles;

				if (roles.length === 0) {
					return (
						<Badge
							color="gray.4"
							variant="outline"
							c="dark.4"
							fw={400}
							data-testid="permission-role-member"
						>
							{tOrganization('role.member')}
						</Badge>
					);
				}

				return <PermissionsCell permissionRoles={roles} t={tOrganization} />;
			},
		},
		{
			cell: ({ row }) => {
				return (
					<ResendInvitationButton
						user={row.original}
						onResendInvitation={handleResendInvitation}
					/>
				);
			},
			header: '',
			id: 'actions',
			enableGlobalFilter: false,
			enableColumnFilter: false,
			filter: () => <div />,
			size: 200,
			meta: {
				isGrow: true,
			},
		},
	];

	const mantineTableDataOperations = useMantineTableDataOperations();

	if (isFetching) {
		return <InlineLoader />;
	}

	if (!data) {
		return <GenericError />;
	}

	return (
		<Flex
			direction="column"
			h="100%"
			w="100%"
			p="1rem"
			gap="1rem"
			mih={0}
			style={{ flex: 1, overflow: 'hidden' }}
		>
			<Flex justify="flex-end">
				<Button
					leftSection={<Icon variant="add" size="lg" />}
					onClick={() => setShowInviteDrawer(true)}
					data-testid={ORGANIZATION_ELEMENT_ID.TABLED_ROW_INVITE}
				>
					{tOrganization('members.invite_member')}
				</Button>
			</Flex>
			<Flex direction="column" w="100%" mih={0} style={{ flex: 1, overflow: 'hidden' }}>
				<MantineTable<BasicUserWithPermissionRolesRes>
					columns={columns}
					mantineTableBodyRowProps={({ row }) => ({
						onClick: () => onViewChange(OrganizationSettingsView.User, row.original.id),
						style: { cursor: 'pointer', height: 'unset' },
						'data-testid': `${ORGANIZATION_ELEMENT_ID.USER_ROW}-${row.original.email}`,
					})}
					data={data}
					dataOperations={mantineTableDataOperations}
					enableGlobalFilter
					manualFiltering={false}
					manualSorting={false}
					rowCount={data.length}
					enablePagination
					enableStickyHeader
				/>
			</Flex>
			{organization.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					setIsOpen={setShowInviteDrawer}
					onInvite={handleOrganizationInvite}
					isLoading={isPending}
					permissions={permissions}
					description={tOrganization('invite.description')}
					roleLabel={tOrganization('invite.organization_role')}
				/>
			)}
		</Flex>
	);
};
